import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Formik } from "formik";
import * as yup from "yup";
import api from 'state/axios';
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    CircularProgress,
    TextField,
    Button,
    FormControlLabel, 
    Checkbox, 
} from "@mui/material";
import {
    // Add,
    // Delete,
    ArrowBackIosNew,
} from '@mui/icons-material';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de, enGB } from 'date-fns/locale';
import useAuthCheck from "hooks/useAuthCheck";
import { LAYOUT_HEIGHT } from "state/constants";
import { parseUTCDate } from 'state/helpers';
import FlexBetween from "components/FlexBetween";
import FlexCenter from "components/FlexCenter";
import Header from "components/Header";
import StyledButton from 'components/StyledButton';
import CustomDatePicker from "components/CustomDatePicker";
import ContributionRate from "./ContributionRate";


const ContributionPage = () => {
    useAuthCheck();
    const navigate = useNavigate();
    const locales = { 'en-us': undefined, 'en-gb': enGB, de };
    const { contributionId } = useParams();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [contributionTypes, setContributionTypes] = useState([]);
    const contributionCodes = useMemo(() => 
        (contributionTypes || [])?.map(({ contributionCode, description }) => ({ contributionCode, description })
    ), [contributionTypes]);


    const [formValues, setFormValues] = useState({
        contributionObject: "",
        description: "",        
        contributionRate: [],        
        effectiveFrom: new Date(),
        effectiveTo: null,        
        sortOrder: 0,
        isActive: true,
    });


    const fetchContributionTypes = useCallback(async () => {
        const url = '/contributiontypes';
        return await api.get(url); 
    }, []);

    const fetchContribution = useCallback(async () => {
        if (contributionId) {
            const url = `/contributions/${contributionId}`;
            return await api.get(url);            
        }
    }, [contributionId]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);
                
                const [contributionTypesResponse,
                    contributionResponse,                    
                ] = await Promise.all([
                    fetchContributionTypes(),
                    contributionId ? fetchContribution() : Promise.resolve(null),
                ]);
                
                setContributionTypes(contributionTypesResponse?.data || []);
                
                const formData = contributionResponse?.data || {};             
                setFormValues({
                    contributionObject: formData.contributionObject || "",                    
                    description: formData.description || "",
                    contributionRate: formData.contributionRate || [],                    
                    effectiveFrom: formData.effectiveFrom ? new Date(formData.effectiveFrom) : null,
                    effectiveTo: formData.effectiveTo ? new Date(formData.effectiveTo) : null,                    
                    sortOrder: formData.sortOrder || 0,
                    isActive: formData.isActive ?? true,
                });                
            } catch (error) {
                setError(error);
                toast.error(error.response?.data?.message || "Failed to fetch data!");
            } finally {
                setLoading(false);
            }
        };
    
        fetchData();      
    }, [fetchContributionTypes,
        fetchContribution, 
        contributionId
    ]); 

    const checkoutSchema = yup.object().shape({
        contributionObject: yup.string().required("Required"),
    });

    const handleFormSubmit = async (values) => {
        try {
            const dateFields = [                
                'effectiveFrom',
                'effectiveTo',
            ];

            const payload = {
                ...values,
                ...dateFields.reduce((acc, field) => {
                    acc[field] = values[field] ? parseUTCDate(values[field]) : null;
                    return acc;
                }, {}),
            };
            
            const response = await api({
                method: contributionId ? "PUT" : "POST",
                url: contributionId
                    ? `/contributions/update/${contributionId}`
                    : '/contributions/new',
                data: payload,
            });

            if (![200, 201].includes(response.status)) {
                toast.error("Failed to submit the form!");
                return;
            }

            toast.success(
                contributionId
                    ? "Contribution updated successfully."
                    : "Contribution created successfully."
            );

            navigate("/contributions");
        } catch (error) {
            setError(error);
            toast.error(error.message || "Failed to submit the form!");
        }
    };

    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Contribution"
                    subtitle={`${loading ? "Loading..."
                        : contributionId
                            ? "Update"
                            : "Create"
                    }`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/contributions")}
                        >
                            <ArrowBackIosNew />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <Typography color="error">Error: {error?.message || ''}</Typography>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: "auto" }}>
                    <LocalizationProvider 
                        dateAdapter={AdapterDateFns}
                        adapterLocale={locales['en-gb']}
                    >
                        <Formik
                            initialValues={formValues}
                            enableReinitialize={true}
                            validationSchema={checkoutSchema}
                            onSubmit={handleFormSubmit}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                                setFieldValue,
                            }) => (
                                <form onSubmit={handleSubmit}>
                                    <Box
                                        display="grid"
                                        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                        gap={2}
                                        sx={{ "& > div": { gridColumn: isDesktop ? undefined : "span 12" } }}
                                    >
                                        <TextField
                                            label="Contribution Object"
                                            name="contributionObject"
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.contributionObject}
                                            error={
                                                !!touched.contributionObject &&
                                                !!errors.contributionObject
                                            }
                                            helperText={
                                                touched.contributionObject &&
                                                errors.contributionObject
                                            }
                                            sx={{ gridColumn: "span 6" }}
                                        />
                                        
                                        <TextField
                                            label="Description"
                                            name="description"
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.description}
                                            error={
                                                !!touched.description &&
                                                !!errors.description
                                            }
                                            helperText={
                                                touched.description &&
                                                errors.description
                                            }
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <CustomDatePicker
                                            label="Effective From"
                                            name="effectiveFrom"
                                            value={values.effectiveFrom}
                                            maxDate={values.effectiveTo}
                                            onChange={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <CustomDatePicker
                                            label="Effective To"
                                            name="effectiveTo"
                                            value={values.effectiveTo}
                                            minDate={values.effectiveFrom}
                                            onChange={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            type="number"
                                            label="Sort Order"
                                            name="sortOrder"
                                            value={values.sortOrder}                                            
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            slotProps={{
                                                input: {
                                                    sx: {
                                                        "& input": { textAlign: "right" },
                                                    },
                                                },
                                            }}
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <FormControlLabel
                                            sx={{ gridColumn: 'span 6' }}
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    name="isActive"
                                                    checked={values.isActive}
                                                    onChange={handleChange}
                                                />
                                            }
                                            label="Is Active"
                                        />

                                        <Typography variant="h6" fontWeight="bold" sx={{ gridColumn: 'span 12' }}>
                                            Contribution Rate
                                        </Typography>                                        
                                        <Box sx={{ gridColumn: 'span 12', ml: 2 }}>
                                            <ContributionRate
                                                values={values}
                                                setFieldValue={setFieldValue}
                                                contributionCodes={contributionCodes}
                                            />
                                        </Box>
                                                                                
                                        <Button
                                            fullWidth
                                            color="secondary"
                                            variant="contained"
                                            type="submit"
                                            sx={{ gridColumn: "span 12", height: 42 }}
                                        >
                                            {contributionId ? "Update" : "Create"}
                                        </Button>
                                    </Box>
                                </form>
                            )}
                        </Formik>
                    </LocalizationProvider>
                </Box>
            )}
        </Box>
    );
};

export default ContributionPage;